import { ChallengeModes } from '../../../constants/shared/challenge-modes';
import { AwsRegions } from '../../../constants/shared/aws-regions';
import { AwsServices } from '../../../constants/shared/aws-services';
import { i18nKeys } from '../../../utils/i18n.utils';
import { LabProvider } from '../../../types/LabProvider';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { ChallengeDifficulty, ChallengeLearningType, ChallengeDomains } from '../../../types/Challenge';

export enum SettingType {
  ALL_IN = 'All In',
  CTF = 'CTF',
}

// Legacy deprecated Domain options
export const SettingTypeDefinitions: OptionDefinition[] = [
  { label: i18nKeys.challenge.optionDefinitions.settingType.allIn, value: SettingType.ALL_IN },
  { label: i18nKeys.challenge.optionDefinitions.settingType.ctf, value: SettingType.CTF },
];

// Know as 'jamType' in the backend
export const SettingDomainDefinitions: OptionDefinition[] = [
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.analytics,
    value: ChallengeDomains.ANALYTICS,
    description: i18nKeys.challenge.optionDefinitions.settingType.analyticsDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.genai,
    value: ChallengeDomains.GENAI,
    description: i18nKeys.challenge.optionDefinitions.settingType.genaiDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.machinelearning,
    value: ChallengeDomains.MACHINE_LEARNING,
    description: i18nKeys.challenge.optionDefinitions.settingType.machinelearningDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.cloudessentials,
    value: ChallengeDomains.CLOUD_ESSENTIALS,
    description: i18nKeys.challenge.optionDefinitions.settingType.cloudessentialsDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.compute,
    value: ChallengeDomains.COMPUTE,
    description: i18nKeys.challenge.optionDefinitions.settingType.computeDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.customerengagement,
    value: ChallengeDomains.CUSTOMER_ENGAGEMENT,
    description: i18nKeys.challenge.optionDefinitions.settingType.customerengagementDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.database,
    value: ChallengeDomains.DATABASE,
    description: i18nKeys.challenge.optionDefinitions.settingType.databaseDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.devops,
    value: ChallengeDomains.DEVOPS,
    description: i18nKeys.challenge.optionDefinitions.settingType.devopsDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.costmanagement,
    value: ChallengeDomains.COST_MANAGEMENT,
    description: i18nKeys.challenge.optionDefinitions.settingType.costmanagementDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.iot,
    value: ChallengeDomains.IOT,
    description: i18nKeys.challenge.optionDefinitions.settingType.iotDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.sysops,
    value: ChallengeDomains.SYSOPS,
    description: i18nKeys.challenge.optionDefinitions.settingType.sysopsDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.serverless,
    value: ChallengeDomains.SERVERLESS,
    description: i18nKeys.challenge.optionDefinitions.settingType.serverlessDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.migration,
    value: ChallengeDomains.MIGRATION,
    description: i18nKeys.challenge.optionDefinitions.settingType.migrationDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.networking,
    value: ChallengeDomains.NETWORKING_AND_CONTENT_DELIVERY,
    description: i18nKeys.challenge.optionDefinitions.settingType.networkingDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.security,
    value: ChallengeDomains.SECURITY,
    description: i18nKeys.challenge.optionDefinitions.settingType.securityDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.storage,
    value: ChallengeDomains.STORAGE,
    description: i18nKeys.challenge.optionDefinitions.settingType.storageDescription,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.settingType.other,
    value: ChallengeDomains.OTHER,
    description: i18nKeys.challenge.optionDefinitions.settingType.otherDescription,
  },
];

export const DifficultyDefinitions: OptionDefinition[] = ChallengeDifficulty.values.map((def) => ({
  label: def.i18nKeyLong,
  value: def.key.toString(),
}));

export const LearningTypeDefinitions: OptionDefinition[] = [
  {
    label: i18nKeys.challenge.optionDefinitions.learningType.team.label,
    value: ChallengeLearningType.TEAM,
    description: i18nKeys.challenge.optionDefinitions.learningType.team.description,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.learningType.individual.label,
    value: ChallengeLearningType.INDIVIDUAL,
    description: i18nKeys.challenge.optionDefinitions.learningType.individual.description,
  },
];

export const ModeDefinitions: OptionDefinition[] = [
  {
    label: i18nKeys.challenge.optionDefinitions.modeType.traditional,
    value: ChallengeModes.TRADITIONAL,
  },
  {
    label: i18nKeys.challenge.optionDefinitions.modeType.castleDefense,
    value: ChallengeModes.CASTLE_DEFENSE,
  },
];

export const LabProviderDefinitions: OptionDefinition[] = [
  {
    label: i18nKeys.challenge.optionDefinitions.labProviderType.awsLabs,
    value: LabProvider.AWS_LABS,
  },
];

export const AWSServicesDefinitions: OptionDefinition[] = [];

export const SupportedRegionsDefinitions: OptionDefinition[] = [];

export const SupportedRegionsDefinitionsWithID: OptionDefinition[] = [];

const setAWSServiceDefinitions = () => {
  const awsServicesList = AwsServices.list.sort();
  awsServicesList.forEach((value) => AWSServicesDefinitions.push({ label: value, value }));
};

const setSupportedRegionsDefinitions = () => {
  AwsRegions.ALL_AWS_REGIONS.forEach((region) =>
    SupportedRegionsDefinitions.push({ label: region.name, value: region.id })
  );
};

const setSupportedRegionsDefinitionsWithID = () => {
  AwsRegions.ALL_AWS_REGIONS.forEach((region) =>
    SupportedRegionsDefinitionsWithID.push({ label: `${region.name} - ${region.id}`, value: region.id })
  );
};

const initDefinitions = () => {
  setAWSServiceDefinitions();
  setSupportedRegionsDefinitions();
  setSupportedRegionsDefinitionsWithID();
};

initDefinitions();
